/**
 * The two scale helpers the fan chart needs.
 *
 * Deliberately not d3-scale. The chart needs exactly one linear mapping and one
 * tick generator, and hand-rolling them keeps a dependency (and its bundle cost)
 * out of a product whose load time is part of its design spec.
 */

export interface LinearScale {
  (value: number): number
  invert(pixel: number): number
  domain: readonly [number, number]
  range: readonly [number, number]
}

export function linearScale(
  domain: readonly [number, number],
  range: readonly [number, number],
): LinearScale {
  const [d0, d1] = domain
  const [r0, r1] = range
  // A zero-width domain would divide by zero; pin it to the range start instead.
  const span = d1 - d0
  const ratio = span === 0 ? 0 : (r1 - r0) / span

  const scale = ((value: number) => r0 + (value - d0) * ratio) as LinearScale
  scale.invert = (pixel: number) => (ratio === 0 ? d0 : d0 + (pixel - r0) / ratio)
  scale.domain = domain
  scale.range = range
  return scale
}

/**
 * Round tick values at a human step size.
 *
 * Steps snap to 1, 2, 2.5 or 5 times a power of ten, which is what makes an axis
 * read "₹1 Cr, ₹2 Cr, ₹3 Cr" instead of "₹0.87 Cr, ₹1.74 Cr". The 2.5 step is
 * included because rupee magnitudes cluster around lakh and crore boundaries and
 * a 2.5 step lands on ₹25 L and ₹2.5 Cr, which are numbers people actually use.
 */
export function niceTicks(min: number, max: number, targetCount: number): number[] {
  if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) return []

  const rawStep = (max - min) / Math.max(1, targetCount)
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)))
  const normalised = rawStep / magnitude

  let step: number
  if (normalised <= 1) step = 1
  else if (normalised <= 2) step = 2
  else if (normalised <= 2.5) step = 2.5
  else if (normalised <= 5) step = 5
  else step = 10
  step *= magnitude

  const ticks: number[] = []
  const first = Math.ceil(min / step) * step
  // The epsilon stops floating-point drift from dropping the final tick.
  for (let v = first; v <= max + step * 1e-6; v += step) {
    ticks.push(Math.abs(v) < step * 1e-6 ? 0 : v)
  }
  return ticks
}
